import { useEffect, useRef } from 'react';

interface Props {
    className?: string;
}

interface Star {
    x: number;
    y: number;
    size: number;
    opacity: number;
    twinkleSpeed: number;
    twinkleOffset: number;
}

/**
 * StarfieldBackground - animated starry sky with nebula effects sized to its container.
 * This is adapted from the Home page background for reuse within the map container.
 */
export function StarfieldBackground({ className = '' }: Props) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const starsRef = useRef<Star[]>([]);
    const animationRef = useRef<number>(0);
    const resizeObsRef = useRef<ResizeObserver | null>(null);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        const canvas = canvasRef.current;
        if (!wrapper || !canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const resizeCanvas = () => {
            const rect = wrapper.getBoundingClientRect();
            canvas.width = Math.max(1, Math.floor(rect.width));
            canvas.height = Math.max(1, Math.floor(rect.height));
            generateStars();
        };

        const generateStars = () => {
            const area = canvas.width * canvas.height;
            const starCount = Math.floor(area / 3000);
            starsRef.current = [];
            for (let i = 0; i < starCount; i++) {
                starsRef.current.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    size: Math.random() * 2 + 0.5,
                    opacity: Math.random() * 0.5 + 0.3,
                    twinkleSpeed: Math.random() * 0.003 + 0.001,
                    twinkleOffset: Math.random() * Math.PI * 2,
                });
            }
        };

        const animate = (time: number) => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            starsRef.current.forEach((star) => {
                const twinkle = Math.sin(time * star.twinkleSpeed + star.twinkleOffset);
                const currentOpacity = star.opacity + twinkle * 0.3;

                ctx.beginPath();
                ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255, 255, 255, ${Math.max(0.1, Math.min(1, currentOpacity))})`;
                ctx.fill();

                if (star.size > 1.5) {
                    ctx.beginPath();
                    ctx.arc(star.x, star.y, star.size * 2, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(200, 220, 255, ${currentOpacity * 0.2})`;
                    ctx.fill();
                }
            });

            animationRef.current = requestAnimationFrame(animate);
        };

        // Observe wrapper size
        resizeObsRef.current = new ResizeObserver(resizeCanvas);
        resizeObsRef.current.observe(wrapper);
        resizeCanvas();
        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (resizeObsRef.current) {
                resizeObsRef.current.disconnect();
                resizeObsRef.current = null;
            }
            cancelAnimationFrame(animationRef.current);
        };
    }, []);

    return (
        <div ref={wrapperRef} className={`overflow-hidden ${className}`}>
            {/* Base sky gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-indigo-950" />

            {/* Nebula overlays */}
            <div className="absolute inset-0">
                <div
                    className="absolute top-0 right-0 w-[60%] h-[50%] opacity-30"
                    style={{
                        background: 'radial-gradient(ellipse at 70% 20%, rgba(139, 92, 246, 0.4) 0%, rgba(139, 92, 246, 0.1) 40%, transparent 70%)',
                    }}
                />
                <div
                    className="absolute bottom-0 left-0 w-[50%] h-[40%] opacity-25"
                    style={{
                        background: 'radial-gradient(ellipse at 30% 80%, rgba(59, 130, 246, 0.4) 0%, rgba(59, 130, 246, 0.1) 40%, transparent 70%)',
                    }}
                />
                <div
                    className="absolute top-1/3 left-1/4 w-[40%] h-[30%] opacity-20"
                    style={{
                        background: 'radial-gradient(ellipse at 50% 50%, rgba(236, 72, 153, 0.3) 0%, rgba(168, 85, 247, 0.1) 50%, transparent 80%)',
                    }}
                />
                <div
                    className="absolute bottom-1/4 right-1/4 w-[30%] h-[25%] opacity-15"
                    style={{
                        background: 'radial-gradient(ellipse at 50% 50%, rgba(20, 184, 166, 0.3) 0%, transparent 70%)',
                    }}
                />
            </div>

            <canvas ref={canvasRef} className="absolute inset-0" />
        </div>
    );
}

export default StarfieldBackground;

